import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  ClickAwayListener,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import { PositionBox } from "./PositionBox";
import { useDepthChart } from "../../../contexts/DepthChartContext";
import { DateTimeOptions } from "../../../services";
import {
  DepthChartPlayer,
  DepthChartPosition,
} from "../../../types/DepthChartTypes";
import { useSchemes } from "../../../contexts/SchemesContext";
import Draggable from "react-draggable";
import { ContentPasteSearchOutlined } from "@mui/icons-material";

interface Props {
  positionAssignments: Record<string, DepthChartPlayer[]>;
  onRemovePlayer?: (positionId: string, playerId: number) => void;
  chartName?: string;
}

export const DepthChartComponent: React.FC<Props> = ({
  positionAssignments,
  onRemovePlayer,
}) => {
  const {
    selectedPersonnelGroup,
    setSelectedPersonnelGroup,
    editMode,
    selectedDepthChart,
    setSelectedDepthChart,
  } = useDepthChart();

  const { offensivePersonnel } = useSchemes();

  const handleTitleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedDepthChart) return;
    
    setSelectedDepthChart({
      ...selectedDepthChart,
      name: event.target.value
    });
  };

  const handleTitleBlur = () => {
    // Optional: Add any validation or formatting logic here
    if (selectedDepthChart?.name.trim() === '') {
      setSelectedDepthChart({
        ...selectedDepthChart,
        name: 'Untitled Chart'
      });
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      // Reset to original name if available
      if (selectedDepthChart) {
        setSelectedDepthChart({
          ...selectedDepthChart,
          name: selectedDepthChart.name
        });
      }
      event.currentTarget.blur();
    }
  };

  const formattedCreatedAt = (created_at: string) => {
    const date = new Date(created_at);
    const formattedDate = new Intl.DateTimeFormat(
      "en-US",
      DateTimeOptions
    ).format(date);
    return formattedDate;
  };

  const displayDate = () => {
    if (selectedDepthChart?.created_at) {
      return formattedCreatedAt(selectedDepthChart?.created_at);
    }
    return "";
  };

  const [draggingId, setDraggingId] = useState<string | null>(null);

  if (!selectedDepthChart) return null;

  console.log("positions", selectedDepthChart.positions);

  // Handler for renaming a position
  const handleRename = (id: string, newName: string) => {
    setSelectedDepthChart(prev => {
      if (!prev) return null;
      return {
        ...prev,
        positions: prev.positions.map(pos =>
          pos.position_id === id ? { ...pos, position_name: newName } : pos
        )
      };
    });
  };

  // Handler for drag stop
  const handleDragStop = (id: string, e: any, data: any) => {
    setSelectedDepthChart(prev => {
      if (!prev) return null;
      return {
        ...prev,
        positions: prev.positions.map(pos =>
          pos.position_id === id ? { ...pos, x: data.x, y: data.y } : pos
        )
      };
    });
    setDraggingId(null);
  };

  // Handler for drag start
  const handleDragStart = (id: string) => {
    setDraggingId(id);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        // alignItems: "center",
        position: "relative",
        height: "100%",
        border: "1px solid rgba(255, 255, 255, 0.12)",
        borderRadius: 2,
        p: 3,
        bgcolor: "#1e1e1e",
        overflow: "auto",
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          mb: 2,
        }}
      >
        {/* Personnel Selector (Left side) when not working on */}
        <Box sx={{ width: "25%", justifyContent: "flex-start", alginItems: "flex-start"}}>
        {!selectedDepthChart.is_template && (
          editMode ? (
            <FormControl sx={{ width: "200px" }}>
              <InputLabel id="personnel-select-label" sx={{ color: "white" }}>
                Personnel
              </InputLabel>
              <Select
                labelId="personnel-select-label"
                id="personnel-select"
                value={selectedPersonnelGroup?.id || ""}
                label="Personnel"
                onChange={(event) => {
                  const selected = offensivePersonnel.find(
                    (group) => group.id === event.target.value
                  );
                  if (selected) {
                    setSelectedPersonnelGroup(selected);
                  }
                }}
                disabled={!editMode}
                sx={{
                  color: "white",
                  ".MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(255, 255, 255, 0.23)",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: editMode
                      ? "rgba(255, 255, 255, 0.5)"
                      : "rgba(255, 255, 255, 0.23)",
                  },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                    borderColor: "white",
                  },
                  ".MuiSvgIcon-root": {
                    color: "white",
                  },
                  "&.Mui-disabled": {
                    color: "rgba(255, 255, 255, 0.5)",
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "rgba(255, 255, 255, 0.15)",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "rgba(255, 255, 255, 0.5)",
                    },
                  },
                }}
              >
                {offensivePersonnel.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography
              variant="subtitle1"
              sx={{
                color: "white",
                width: "200px",
                textAlign: "center",
              }}
            >
              {selectedPersonnelGroup
                ? `${selectedPersonnelGroup?.name} Personnel`
                : ""}
            </Typography>
          )
        )}
        </Box>
        
        <Box sx={{ width: "50%",  justifyContent: "center"}}>
          {editMode && selectedDepthChart ? (
            <ClickAwayListener onClickAway={handleTitleBlur}>
              <TextField
                value={selectedDepthChart?.name}
                onChange={handleTitleChange}
                onBlur={handleTitleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                variant="standard"
                inputProps={{
                  style: {
                    color: "white",
                    textAlign: "center",
                    fontSize: "1.8rem",
                    fontWeight: "bold",
                  },
                }}
                sx={{
                  width: "50%",
                  "& .MuiInput-underline:before": {
                    borderBottomColor: "rgba(255, 255, 255, 0.3)",
                  },
                  "& .MuiInput-underline:hover:before": {
                    borderBottomColor: "rgba(255, 255, 255, 0.5)",
                  },
                  "& .MuiInput-underline:after": {
                    borderBottomColor: "white",
                  },
                }}
              />
            </ClickAwayListener>
          ) : (
            <Typography
              variant="h4"
              sx={{
                color: "white",
                cursor: editMode ? "pointer" : "default",
                "&:hover": {
                  backgroundColor: editMode
                    ? "rgba(255, 255, 255, 0.1)"
                    : "transparent",
                  borderRadius: 1,
                },
                padding: "4px 8px",
                display: "inline-block",
              }}
            >
              {selectedDepthChart?.name}
            </Typography>
          )}
        </Box>

        {/* Date/Time (Right side) */}
        <Box sx={{ width: "25%",  justifyContent: "flex-end", alginItems: "flex-end"}}>
        <Typography
          variant="body2"
          sx={{
            color: "rgba(255, 255, 255, 0.7)",
            fontSize: "1rem",
            fontFamily: "monospace",
            width: "200px",
            textAlign: "right",
          }}
        >
          {displayDate()}
        </Typography>
        </Box>
      </Box>

      {/* chart area */}
      <Box
        sx={{
          position: "relative",
          minWidth: 1200,
          minHeight: 490,
          border: "1px solid rgba(255,255,255,0.1)",
          bgcolor: "#232323",
          // overflow: "auto",
        }}
      >
        {selectedDepthChart.positions.map((position) =>
          editMode ? (
            <Draggable
              key={position.position_id}
              position={{ x: position.x, y: position.y }}
              onStart={() => handleDragStart(position.position_id)}
              onStop={(e, data) => handleDragStop(position.position_id, e, data)}
              bounds="parent"
            >
              <Box
                sx={{
                  position: "absolute",
                  zIndex: draggingId === position.position_id ? 9999 : 2,
                }}
              >
                <PositionBox
                  position={position}
                  players={positionAssignments[position.position_id] || []}
                  onRemovePlayer={onRemovePlayer}
                  isEditable={editMode}
                  onRename={handleRename}
                />
              </Box>
            </Draggable>
          ) : (
            <Box
              key={position.position_id}
              sx={{
                position: "absolute",
                left: position.x,
                top: position.y,
                zIndex: 2,
              }}
            >
              <PositionBox
                position={position}
                players={positionAssignments[position.position_id] || []}
                isEditable={false}
              />
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};
